import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { SITE_ORIGIN } from '../config/site';
import { createLocalizedUrl } from '../utils/languageUtils';
import SiteHeader from './SiteHeader';
import Footer from './Footer';
import { 
    HomeIcon, 
    BookOpenIcon, 
    WrenchScrewdriverIcon, 
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const NotFound = () => {
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const location = useLocation();

    // Determine context from URL path
    const getContextInfo = () => {
        const path = location.pathname;
        const pathWithoutLang = path.replace(/^\/(en|es|fr)\//, '/');
        
        if (pathWithoutLang.startsWith('/guides/')) {
            return {
                type: 'article',
                title: t('notFound.article.title'),
                description: t('notFound.article.description'),
                primaryAction: { 
                    text: t('notFound.article.primaryAction'), 
                    url: createLocalizedUrl('/guides', currentLanguage),
                    icon: BookOpenIcon
                },
                secondaryAction: { 
                    text: t('notFound.article.secondaryAction'), 
                    url: createLocalizedUrl('/tools', currentLanguage),
                    icon: WrenchScrewdriverIcon
                }
            };
        } else if (pathWithoutLang.startsWith('/tools/')) {
            return {
                type: 'tool',
                title: t('notFound.tool.title'),
                description: t('notFound.tool.description'),
                primaryAction: { 
                    text: t('notFound.tool.primaryAction'), 
                    url: createLocalizedUrl('/tools', currentLanguage),
                    icon: WrenchScrewdriverIcon
                },
                secondaryAction: { 
                    text: t('notFound.tool.secondaryAction'), 
                    url: createLocalizedUrl('/guides', currentLanguage),
                    icon: BookOpenIcon
                }
            };
        } else {
            return {
                type: 'general',
                title: t('notFound.general.title'),
                description: t('notFound.general.description'),
                primaryAction: { 
                    text: t('notFound.general.primaryAction'), 
                    url: createLocalizedUrl('/', currentLanguage),
                    icon: HomeIcon
                },
                secondaryAction: { 
                    text: t('notFound.general.secondaryAction'), 
                    url: createLocalizedUrl('/help-spain', currentLanguage),
                    icon: MagnifyingGlassIcon
                }
            };
        }
    };
    
    const contextInfo = getContextInfo();
    const PrimaryIcon = contextInfo.primaryAction.icon;
    const SecondaryIcon = contextInfo.secondaryAction.icon;

    // Get translated popular links
    const getPopularLinks = () => {
        if (contextInfo.type === 'article') {
            return [
                { name: t('notFound.popularLinks.immigrationGuides'), url: createLocalizedUrl('/visas-permits', currentLanguage) },
                { name: t('notFound.popularLinks.taxInformation'), url: createLocalizedUrl('/taxes', currentLanguage) },
                { name: t('notFound.popularLinks.bankingFinance'), url: createLocalizedUrl('/finance', currentLanguage) },
                { name: t('notFound.popularLinks.cityGuides'), url: createLocalizedUrl('/cities', currentLanguage) }
            ];
        } else if (contextInfo.type === 'tool') {
            return [
                { name: t('notFound.popularLinks.costExplorer'), url: createLocalizedUrl('/cost-explorer', currentLanguage) },
                { name: t('notFound.popularLinks.residencyRouteFinder'), url: createLocalizedUrl('/tools', currentLanguage) },
                { name: t('notFound.popularLinks.ipremCalculator'), url: createLocalizedUrl('/tools', currentLanguage) },
                { name: t('notFound.popularLinks.allTools'), url: createLocalizedUrl('/tools', currentLanguage) }
            ];
        } else {
            return [
                { name: t('common.guides'), url: createLocalizedUrl('/guides', currentLanguage) },
                { name: t('common.tools'), url: createLocalizedUrl('/tools', currentLanguage) },
                { name: t('common.helpCenter'), url: createLocalizedUrl('/help-spain', currentLanguage) },
                { name: t('common.compareSpanishCities'), url: createLocalizedUrl('/cost-explorer', currentLanguage) }
            ];
        }
    };

    const popularLinks = getPopularLinks();

    return (
        <>
            <Helmet>
                <html lang={currentLanguage} />
                <title>{contextInfo.title}</title>
                <meta name="description" content={contextInfo.description} />
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href={`${SITE_ORIGIN}${contextInfo.primaryAction.url}`} />
                <meta property="og:title" content={`${contextInfo.title} | LiveLoveSpain`} />
                <meta property="og:description" content={contextInfo.description} />
                <meta property="og:url" content={`${SITE_ORIGIN}${contextInfo.primaryAction.url}`} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${contextInfo.title} | LiveLoveSpain`} />
                <meta name="twitter:description" content={contextInfo.description} />
            </Helmet>
            
            <div className="min-h-screen flex flex-col bg-gray-50">
                {/* Header */}
                <SiteHeader />

                {/* Main Content */}
                <main className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24" role="main">
                    <div className="max-w-4xl w-full">
                        {/* Error Code */}
                        <div className="text-center mb-12">
                            <h1 className="text-8xl sm:text-9xl font-extrabold text-gray-200 mb-4">
                                404
                            </h1>
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                                {contextInfo.title}
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                {contextInfo.description}
                            </p>
                        </div>

                        {/* Primary Actions */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                            <Link
                                to={contextInfo.primaryAction.url}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
                            >
                                <PrimaryIcon className="h-5 w-5" />
                                {contextInfo.primaryAction.text}
                            </Link>
                            <Link
                                to={contextInfo.secondaryAction.url}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                <SecondaryIcon className="h-5 w-5" />
                                {contextInfo.secondaryAction.text}
                            </Link>
                        </div>

                        {/* Popular Links */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {t('notFound.suggestion')}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {popularLinks.map((link, index) => (
                                    <Link
                                        key={index}
                                        to={link.url}
                                        className="group flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="text-gray-700 group-hover:text-blue-600 font-medium transition-colors">
                                            {link.name}
                                        </span>
                                        <svg 
                                            className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </main>

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default NotFound;
